<template>
  <div class="flex flex-col">
    <!-- Toolbar -->
    <div class="flex items-start justify-between px-1 pb-4">
      <div>
        <h3 class="text-sm font-semibold text-gray-900">Activity</h3>
        <p class="text-xs text-gray-500 mt-0.5">
          Comments and edits across all requirements, newest first.
        </p>
      </div>
      <label class="inline-flex items-center gap-2 text-xs text-gray-600 cursor-pointer shrink-0">
        <input
          v-model="customerOnly"
          type="checkbox"
          class="rounded border-gray-300 h-3.5 w-3.5"
          @change="reload"
        />
        Customer activity only
      </label>
    </div>

    <!-- Loading -->
    <div v-if="loading && !events.length" class="space-y-3 animate-pulse px-1">
      <div v-for="n in 6" :key="n" class="flex items-start gap-3">
        <div class="w-6 h-6 rounded-full bg-gray-200 shrink-0" />
        <div class="flex-1 space-y-1.5">
          <div class="h-3.5 w-2/3 bg-gray-200 rounded" />
          <div class="h-3 w-1/3 bg-gray-100 rounded" />
        </div>
      </div>
    </div>

    <!-- Unavailable (endpoint not deployed yet) -->
    <div
      v-else-if="unavailable"
      class="text-center text-sm text-gray-400 py-12 px-4"
    >
      <FontAwesomeIcon :icon="['fas', 'wave-square']" class="text-2xl text-gray-300 mb-2" />
      <p>The activity feed isn't available yet.</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="text-center text-sm text-gray-500 py-12">
      Couldn't load activity.
      <button class="text-primary-600 hover:underline ml-1" @click="reload">Retry</button>
    </div>

    <!-- Empty -->
    <div v-else-if="!events.length" class="text-center text-sm text-gray-400 py-12">
      No activity yet.
    </div>

    <!-- Feed -->
    <div v-else class="space-y-6 px-1">
      <div v-for="group in groupedEvents" :key="group.key">
        <div class="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
          {{ group.label }}
        </div>
        <ul class="space-y-0.5">
          <li
            v-for="e in group.events"
            :key="e.id"
            class="flex items-start gap-3 py-2 px-2 rounded hover:bg-gray-50 cursor-pointer"
            @click="openRequirement(e)"
          >
            <span
              class="mt-0.5 w-6 h-6 rounded-full flex items-center justify-center text-[10px] shrink-0"
              :class="iconClass(e)"
            >
              <FontAwesomeIcon :icon="iconFor(e)" />
            </span>
            <div class="min-w-0 flex-1">
              <div class="text-sm text-gray-800">
                <span class="font-medium">{{ e.user_name || 'Someone' }}</span>
                {{ ' ' }}{{ summary(e) }}
                <span
                  v-if="e.is_customer_origin"
                  class="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 align-middle"
                >Customer</span>
              </div>
              <div
                v-if="commentPreview(e)"
                class="text-xs text-gray-600 bg-gray-50 rounded px-2 py-1.5 mt-1 whitespace-pre-wrap line-clamp-2"
              >{{ commentPreview(e) }}</div>
              <div class="text-xs text-gray-500 mt-0.5 truncate">
                <span class="font-mono text-gray-400">#{{ e.requirement }}</span>
                {{ ' ' }}{{ e.requirement_title }}
                <span
                  v-if="e.requirement_type_name"
                  class="ml-1 text-[10px] px-1.5 py-0.5 rounded align-middle"
                  :class="typeClass(e.requirement_type_name)"
                >{{ e.requirement_type_name }}</span>
              </div>
            </div>
            <time
              class="text-[11px] text-gray-400 shrink-0"
              :title="formatDateTime(e.created_at)"
            >{{ relativeTime(e.created_at) }}</time>
          </li>
        </ul>
      </div>

      <div v-if="hasMore" class="pt-1">
        <button
          class="text-xs text-primary-600 hover:underline disabled:text-gray-300"
          :disabled="loading"
          @click="loadMore"
        >
          {{ loading ? 'Loading…' : 'Load more' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { TYPE_CLASSES } from './support_constants';

const PAGE_SIZE = 50;

export default {
  name: 'Support_Activity',
  components: { FontAwesomeIcon },
  data() {
    return {
      events: [],
      loading: true,
      error: false,
      unavailable: false,
      customerOnly: false,
      offset: 0,
      hasMore: false,
    };
  },
  computed: {
    groupedEvents() {
      const groups = [];
      const byKey = {};
      for (const e of this.events) {
        const key = this.dayKey(e.created_at);
        if (!byKey[key]) {
          byKey[key] = { key, label: this.dayLabel(e.created_at), events: [] };
          groups.push(byKey[key]);
        }
        byKey[key].events.push(e);
      }
      return groups;
    },
  },
  mounted() {
    this.reload();
  },
  methods: {
    async reload() {
      this.offset = 0;
      this.events = [];
      this.error = false;
      this.unavailable = false;
      await this.load();
    },
    async loadMore() {
      this.offset += PAGE_SIZE;
      await this.load();
    },
    async load() {
      this.loading = true;
      try {
        const params = { limit: PAGE_SIZE, offset: this.offset };
        if (this.customerOnly) params.customer_origin = true;
        const { data } = await axios.get('/api/requirements/activity/', { params });
        // Contract: DRF paginated { count, results }. Tolerate a plain array too.
        const rows = Array.isArray(data) ? data : data.results || [];
        this.events = this.offset === 0 ? rows : [...this.events, ...rows];
        this.hasMore = Array.isArray(data)
          ? rows.length === PAGE_SIZE
          : this.events.length < (data.count ?? 0);
      } catch (err) {
        if (err?.response?.status === 404) {
          this.unavailable = true;
        } else {
          this.error = true;
        }
      } finally {
        this.loading = false;
      }
    },
    openRequirement(e) {
      if (!e.requirement) return;
      this.$router.push({ path: `/support/service-desk/${e.requirement}` });
    },
    commentPreview(e) {
      if (e.activity_type !== 'commented') return '';
      return e.metadata?.body || e.description || '';
    },
    summary(e) {
      if (e.description) return e.description;
      const map = {
        commented: 'commented',
        created: 'created this',
        updated: 'edited this',
        status_changed: 'changed the status',
        assigned: 'changed the assignee',
        completed: 'completed this',
        reopened: 'reopened this',
        linked: 'linked this',
        unlinked: 'unlinked this',
        tagged: 'tagged this',
        deleted: 'deleted this',
      };
      return map[e.activity_type] || e.activity_type_display || 'updated this';
    },
    iconFor(e) {
      const map = {
        commented: ['fas', 'comment'],
        created: ['fas', 'plus'],
        status_changed: ['fas', 'arrow-right-arrow-left'],
        assigned: ['fas', 'user'],
        completed: ['fas', 'check'],
        reopened: ['fas', 'rotate-left'],
        linked: ['fas', 'link'],
        tagged: ['fas', 'tag'],
      };
      return map[e.activity_type] || ['fas', 'pen'];
    },
    iconClass(e) {
      if (e.is_customer_origin) return 'bg-blue-50 text-blue-600';
      if (e.activity_type === 'commented') return 'bg-primary-50 text-primary-600';
      if (e.activity_type === 'completed') return 'bg-green-50 text-green-600';
      return 'bg-gray-100 text-gray-500';
    },
    typeClass(name) {
      return TYPE_CLASSES[name] || 'bg-gray-100 text-gray-600';
    },
    dayKey(dateStr) {
      const d = new Date(dateStr);
      return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    },
    dayLabel(dateStr) {
      const d = new Date(dateStr);
      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);
      if (this.dayKey(dateStr) === this.dayKey(today)) return 'Today';
      if (this.dayKey(dateStr) === this.dayKey(yesterday)) return 'Yesterday';
      return d.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    },
    relativeTime(dateStr) {
      if (!dateStr) return '';
      const mins = Math.floor((Date.now() - new Date(dateStr)) / 60000);
      if (mins < 1) return 'now';
      if (mins < 60) return `${mins}m`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h`;
      const days = Math.floor(hrs / 24);
      if (days < 7) return `${days}d`;
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },
    formatDateTime(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    },
  },
};
</script>
